/**
 * Game-path hero sprite rendering (M2 sprite pipeline → the real game).
 *
 * The hero replaced the player rectangle placeholder: each player renders as
 * the 8-dir hero sprite at their (cosmetically interpolated) fractional
 * position, tinted per player, with a name tag above the head and a ring
 * under the local player. Two halves:
 *
 *  - Pure helpers (unit-testable, no DOM): `tintForPlayer`, `dirForDelta`,
 *    `walkFrame` — the walk cycle is distance-driven at the same stride as
 *    the lab (`WALK_CYCLES_PER_TILE = 0.5` → 6 frames per 2 tiles), which
 *    matches the sim's walk pace (`MOVE_COST_TICKS`, set via the `MOVE_SPEED`
 *    knob in shared/constants.ts).
 *  - DOM/asset side: `loadHeroAssets()` fetches the committed atlas over
 *    `/sprites/` (copied into dist by build-client.js), and
 *    `heroFrameImg()` resolves frame paths with a cache + a never-bail
 *    last-drawn fallback so a transient load gap shows the previous frame
 *    instead of a disappearing hero (the sprite-pop lesson from the lab).
 *
 * Rendering only — the client decides nothing the sim cares about.
 */
import { DIRS, frameUrl, parseAtlas } from "./atlas.js";
import { applyLutToPixels, type HeroLut } from "./heroLut.js";
import type { Atlas, Dir, HeroOutfit, HeroSprite, Outfit, PartBounds } from "./atlas.js";
import { DIRS_CW } from "./dirs.js";
import { EQUIP_SLOTS, ITEMS, normalizeOutfitSlots } from "@pd/shared";
import type { EquipSlot } from "@pd/shared";
import { frameForCycle } from "./anim.js";
import { spriteStore } from "./spriteStore.js";

/** One full 6-frame stride spans 2 tiles (contact at f0/f3). Same as the lab. */
export const WALK_CYCLES_PER_TILE = 0.5;
/** The walk anim has 6 frames at 8 fps (atlas canon). */
export const WALK_FRAMES = 6;
/** Idle breathing loop: 2 frames at 2 fps (atlas canon). */
export const IDLE_FPS = 2;
export const IDLE_FRAMES = 2;

/**
 * Deterministic per-player tint slot from the atlas's tint list. Room slots
 * are small integers; cycling in insertion order keeps every player visually
 * distinct until the list wraps (capacity is small — modulo is fine).
 */
export function tintForPlayer(tints: readonly string[], playerId: number): string {
  if (tints.length === 0) return "canon";
  const i = ((playerId % tints.length) + tints.length) % tints.length;
  return tints[i] ?? "canon";
}

/** Number of player-identity tints the atlas carries for the personal
 *  gatestone (p0..p4). Matches the generator's PLAYER_TINTS — the lobby
 *  caps rooms at 5 players. Keep in sync with tools/sprites/gen.py. */
export const PLAYER_TINT_COUNT = 5;

/** Personal gatestone tint name for a player id: their identity colour
 *  (same golden-angle hue as playerColor). */
export function playerTint(playerId: number): string {
  const i = ((playerId % PLAYER_TINT_COUNT) + PLAYER_TINT_COUNT) % PLAYER_TINT_COUNT;
  return `p${i}`;
}

/**
 * Screen-space facing for a tile-coord movement delta (same mapping as the
 * lab's dirs.ts): +x tile → se (screen down-right), +y tile → sw. Null for a
 * zero delta (keep the current facing).
 */
export function dirForDelta(dx: number, dy: number): Dir | null {
  if (dx === 0 && dy === 0) return null;
  const sx = dx - dy;
  const sy = dx + dy;
  const angle = Math.atan2(sy, sx);
  const idx = Math.round((angle + Math.PI / 2) / (Math.PI / 4));
  const i = ((idx % 8) + 8) % 8;
  return DIRS_CW[i] ?? "s";
}

/** Diagonal → cardinal fold for fewer-direction NPCs (the 4-dir dragon
 *  boss: n/s/e/w). The iso projection maps axis tile moves onto the screen
 *  diagonals (+x tile → se, +y tile → sw, …), so the 8-way facing math above
 *  hands a 4-dir NPC a diagonal it has no frames for. Fold to the dominant
 *  SCREEN axis of the movement — tie goes HORIZONTAL, so a quadruped crossing
 *  the field of view keeps its readable profile instead of snapping to its
 *  front. Every observed walk delta is a single tile (bigger snaps as a
 *  teleport, no walk anim) and each single-tile delta lands exactly on a
 *  diagonal-sector boundary, so this static fold is exactly the delta-based
 *  dominant-axis fold for every delta that can occur.
 */
export const DIR_FOLD: Partial<Record<Dir, Dir>> = {
  se: "e", sw: "w", ne: "e", nw: "w",
};

/** The dir to actually render for an NPC whose anim only carries `available`
 *  dirs: the computed facing itself, else its cardinal fold, else the first
 *  available dir (defensive — a validated atlas never gets here). */
export function foldDir(dir: Dir, available: readonly Dir[]): Dir | null {
  if (available.includes(dir)) return dir;
  const f = DIR_FOLD[dir];
  if (f !== undefined && available.includes(f)) return f;
  const last = available[0];
  return last !== undefined ? last : null;
}

/** Walk-cycle frame index for a distance walked in tiles (loops forever). */
export function walkFrame(walkDistTiles: number): number {
  return frameForCycle(walkDistTiles, WALK_CYCLES_PER_TILE, WALK_FRAMES);
}

/** Per-entity animation state (persists across frames; the viewport owns it).
 * Used by players (game) and enemies (arena) alike. */
export interface PlayerAnim {
  dir: Dir;
  /** Accumulated lerped distance in tiles; drives the walk cycle. */
  walkDist: number;
  /** Idle clock accumulator (ms); drives the breathing loop. */
  idleT: number;
  /** When set, the entity is inside its attack-anim window: render the `attack`
   *  anim at this frame (0..5), overriding walk/idle. The window is the six
   *  frames at natural ATTACK_FPS anchored so the strike frame lands on the
   *  swing's impactTick (see applyCombatAnims); outside it — the idle lead-in
   *  and the post-anim tail — the field is unset and walk/idle renders.
   *  Set/cleared per-frame by applyCombatAnims. */
  attackFrame?: number;
  /** Authoritative heavy-attack windup progress (0..1); unset at impact. */
  heavyWindup?: number;
  pounceFrame?: number;
  pounceHeight?: number;
  pounceWindup?: number;
  pounceElement?: "fire";
  summonFrame?: number;
  meteorWindup?: number;
}

/** Idle frame index for the accumulated idle time. */
export function idleFrame(idleTMs: number): number {
  const per = 1000 / IDLE_FPS;
  return Math.floor(Math.max(0, idleTMs) / per) % IDLE_FRAMES;
}

/** Step a player's anim state by one render frame. Pure over the inputs. */
export function stepPlayerAnim(
  prev: PlayerAnim,
  dtMs: number,
  movedDistTiles: number,
  delta: { dx: number; dy: number },
): PlayerAnim {
  const dir = dirForDelta(delta.dx, delta.dy) ?? prev.dir;
  if (movedDistTiles > 0) {
    return { dir, walkDist: prev.walkDist + movedDistTiles, idleT: 0 };
  }
  return { dir, walkDist: 0, idleT: prev.idleT + dtMs };
}

// ---- DOM / asset side -----------------------------------------------------

export interface HeroAssets {
  atlas: Atlas;
  hero: HeroSprite;
  imgCache: Map<string, HTMLImageElement>;
  /** Slot → last successfully drawn image (never-bail fallback). */
  lastDrawn: Map<string, HTMLImageElement>;
}

function imgFor(assets: HeroAssets, path: string): HTMLImageElement | null {
  const packed = spriteStore();
  if (packed?.has(path)) {
    const im = packed.image(path);
    return im.complete && im.naturalWidth > 0 ? im : null;
  }
  let im = assets.imgCache.get(path);
  if (!im) {
    im = new Image();
    im.src = frameUrl(path, assets.atlas.version);
    assets.imgCache.set(path, im);
  }
  return im.complete && im.naturalWidth > 0 ? im : null;
}

/** Remember `im` as the slot's fallback, or hold the last frame that drew.
 *
 * Every frame lookup in this file goes through here so a load gap costs a stale
 * frame instead of a missing limb. Two things made the packed path used to skip
 * the fallback "obviously unnecessary": pack entries decode up-front, and the
 * fallback's own liveness was never checked. The sprite store's LRU says
 * otherwise — it hands back a 36,962-file pack and drops entries by the
 * hundreds — and an evicted element has its `src` cleared, so it reports
 * `naturalWidth === 0` forever. Holding that paints nothing for the rest of the
 * session, which is worse than the gap it replaced. */
function holdFrame(
  assets: HeroAssets,
  slot: string,
  im: HTMLImageElement | null,
): HTMLImageElement | null {
  if (im) {
    assets.lastDrawn.set(slot, im);
    return im;
  }
  const keep = assets.lastDrawn.get(slot);
  return keep && keep.complete && keep.naturalWidth > 0 ? keep : null;
}

/** Frame image for a tint/anim/dir/frame slot, falling back to the last
 *  drawn image for the slot so a transient load gap never blanks the hero. */
export function heroFrameImg(
  assets: HeroAssets,
  tint: string,
  anim: string,
  dir: Dir,
  frame: number,
): HTMLImageElement | null {
  const paths = assets.hero.frames[tint]?.[anim]?.[dir];
  if (!paths?.length) return null;
  const p = paths[Math.min(frame, paths.length - 1)];
  if (!p) return null;
  return holdFrame(assets, `${tint}:${anim}:${dir}`, imgFor(assets, p));
}

// ---- Outfit compositing (v6 part pipeline) --------------------------------

/** One part-frame to draw: which unit (slot/variant) and which file layer.
 *  `z` is the outfit.layers position this contribution occupies. */
export interface HeroPart {
  slot: string;
  variant: string;
  /** File layer under the unit ("body" for body parts; the z-layer name for
   *  equip layers). */
  layer: string;
  /** z-order position (index into outfit.layers, after weapon resolution). */
  z: string;
}

/** The bare hero — every in-game player renders this, tinted per player. */
export function defaultOutfit(outfit: HeroOutfit): Outfit {
  const o: Outfit = {};
  for (const [slot, v] of Object.entries(outfit.defaults)) o[slot] = v;
  return o;
}

/**
 * Resolve the ordered part list for one (outfit, equipped, dir, tint).
 * CLIENT MIRROR OF THE PIPELINE'S CompositeTree (tools/heropipeline/composite.py):
 * the same z-order, coverage and directional depth rules, the same
 * slotsOrder within a shared layer — all driven by the outfit data, so the
 * client and the generator can never disagree about compositing.
 *
 * Body parts (slot "body") always contribute; their z position is the part
 * name ("legs", "torso", ...). Equip units contribute every file layer their
 * tint's frame data has (cape panel and quiver land in their own layers via
 * the data — no code here knows about specific variants).
 */
export function outfitParts(
  hero: HeroSprite,
  equipped: Outfit,
  dir: Dir,
  tint: string,
): HeroPart[] {
  const outfit = hero.outfit;
  if (!outfit) return [];
  const frames = outfit.frames;
  // any tint shares the unit/layer set (tints are exact remaps); the requested
  // tint first, then the first available (lab previews may query pre-load)
  const tintFrames = frames[tint] ?? frames[Object.keys(frames)[0] ?? ""];
  if (!tintFrames) return [];

  const covered = new Set<string>();
  for (const slot of outfit.slotsOrder) {
    const variant = equipped[slot];
    if (!variant || !tintFrames[slot]?.[variant]) continue;
    for (const hidden of outfit.covers?.[variant] ?? []) covered.add(hidden);
  }

  const contrib = new Map<string, HeroPart[]>();
  const push = (z: string, part: HeroPart): void => {
    const list = contrib.get(z) ?? [];
    list.push(part);
    contrib.set(z, list);
  };
  // body pseudo-slot: always on, z = part name
  const body = tintFrames["body"];
  if (body) {
    for (const variant of Object.keys(body)) push(variant, { slot: "body", variant, layer: "body", z: variant });
  }
  for (const slot of outfit.slotsOrder) {
    if (covered.has(slot)) continue;
    const variant = equipped[slot] ?? null;
    if (!variant) continue;
    const perLayer = tintFrames[slot]?.[variant];
    if (!perLayer) continue;
    for (const layer of Object.keys(perLayer)) {
      const z = outfit.layerDirs?.[layer]?.[dir] ?? layer;
      push(z, { slot, variant, layer, z });
    }
  }

  const behind = outfit.weaponBehindDirs.includes(dir);
  const shieldBehind = (outfit.shieldBehindDirs ?? []).includes(dir);
  const out: HeroPart[] = [];
  for (const z of outfit.layers) {
    if (z === "weapon-behind") {
      if (behind) out.push(...(contrib.get("weapon") ?? []));
    } else if (z === "weapon-front") {
      if (!behind) out.push(...(contrib.get("weapon") ?? []));
    } else if (z === "shield-behind") {
      if (shieldBehind) out.push(...(contrib.get("shield") ?? []));
    } else if (z === "shield-front") {
      if (!shieldBehind) out.push(...(contrib.get("shield") ?? []));
    } else {
      out.push(...(contrib.get(z) ?? []));
    }
  }
  return out;
}

/** Raster geometry never changes the body rig, world anchor or hit area. */
export function heroPartBounds(hero: HeroSprite, p: HeroPart): PartBounds {
  return hero.outfit?.partBounds?.[`${p.slot}/${p.variant}/${p.layer}`] ?? { dims: hero.dims, offset: [0, 0] };
}

/** Include every facing to keep the preview/canvas origin stable during a turn. */
export function heroOutfitBounds(hero: HeroSprite, equipped: Outfit, tint = "canon"): PartBounds {
  let left = 0, top = 0, right = hero.dims[0], bottom = hero.dims[1];
  for (const dir of DIRS) {
    for (const p of outfitParts(hero, equipped, dir, tint)) {
      const { dims: [w, h], offset: [x, y] } = heroPartBounds(hero, p);
      left = Math.min(left, x); top = Math.min(top, y);
      right = Math.max(right, x + w); bottom = Math.max(bottom, y + h);
    }
  }
  return { dims: [right - left, bottom - top], offset: [left, top] };
}

/**
 * The feet anchor in CANVAS PIXELS for a canvas sized `bounds.dims × scale` — draw
 * the hero at this point (plus a cell origin) and the sprite fills that canvas
 * exactly, standing on its bottom edge.
 *
 * The one reason it is a function instead of a line at each call site: `drawHero`
 * takes `cx/cy` already in canvas px and multiplies the anchor by `scale` itself, so
 * a caller that passes LOGICAL px lands the sprite a whole `anchor × scale` off its
 * box — which is how the player card drew its portrait under its own canvas top edge
 * and lost the head (traps/coordinate-space.md). This is the creator preview's
 * framing; every panel portrait follows it.
 */
export function outfitAnchorPx(hero: HeroSprite, bounds: PartBounds, scale: number): [number, number] {
  return [(hero.anchor[0] - bounds.offset[0]) * scale, (hero.anchor[1] - bounds.offset[1]) * scale];
}

/** Catalogue-wide visual overhang, for camera culling before resolving heroes. */
export function heroVisualExtent(hero: HeroSprite): [number, number] {
  const [ax, ay] = hero.anchor;
  let x = Math.max(ax, hero.dims[0] - ax), y = Math.max(ay, hero.dims[1] - ay);
  for (const { dims: [w, h], offset: [ox, oy] } of Object.values(hero.outfit?.partBounds ?? {})) {
    x = Math.max(x, Math.abs(ox - ax), Math.abs(ox + w - ax));
    y = Math.max(y, Math.abs(oy - ay), Math.abs(oy + h - ay));
  }
  return [x, y];
}

/** Part-frame image with the last-drawn fallback for transient pose-load gaps.
 * A cold or evicted part returns null, so incomplete composites retry. */
function heroPartImg(
  assets: HeroAssets,
  path: string,
  slotKey: string,
): HTMLImageElement | null {
  return holdFrame(assets, slotKey, imgFor(assets, path));
}

/**
 * The Outfit the sim says a player is wearing: the bare hero (the outfit's
 * defaults) plus the heroSlot/heroVariant override for each occupied
 * equipment slot (the catalog in shared/items.ts). Client-side mirror of the
 * hero pipeline's part assembly — z-order stays data-driven in outfitParts,
 * this only fills the slots. Missing/unknown kinds are skipped, never throw.
 */
/**
 * The Outfit an account's SAVED look says the player wears: the bare hero's
 * defaults with the look's variants applied on top. Cosmetic truth until the
 * gear build — sim equipment (below) still overwrites the slots it maps to,
 * so a carried sword beats a saved staff. Graceful degradation: a variant
 * the committed atlas no longer has (art regen, stale account) is dropped to
 * the slot's default, never a missing part. Unknown slots are ignored.
 *
 * An EXPLICIT null/empty for a known slot is the creator's "none" — the part
 * is unequipped (a bare chest is a look). An ABSENT slot falls back to the
 * bare hero's default (the creator never sends equipment slots, and old
 * accounts predate the body slots entirely). This must stay in lockstep with
 * the preview, which renders `pv.outfit` where null already means
 * unequipped — one meaning of null, or the game and the creator disagree.
 */
export function outfitForLook(
  hero: HeroSprite,
  lookOutfit: Record<string, string | null> | null | undefined,
): Outfit {
  const o: Outfit = {};
  if (hero.outfit) {
    for (const [slot, v] of Object.entries(hero.outfit.defaults)) o[slot] = v;
    const slots = hero.outfit.slots;
    for (const [slot, v] of Object.entries(normalizeOutfitSlots(lookOutfit))) {
      const known = slots[slot];
      if (!known) continue;
      if (v === null || v === "") {
        o[slot] = null;
        continue;
      }
      if (known.includes(v)) o[slot] = v;
    }
  }
  return o;
}

export function outfitForEquipment(
  hero: HeroSprite,
  equipment: Record<EquipSlot, number | null> | null | undefined,
  catalog: readonly { kind: number; heroSlot: string; heroVariant: string }[] = ITEMS,
  /** Start from this base instead of the bare hero's defaults (a saved
   *  look, via outfitForLook) — equipment still wins over the base. */
  base?: Outfit,
): Outfit {
  const o: Outfit = {};
  if (base) {
    for (const [slot, v] of Object.entries(base)) o[slot] = v;
  } else if (hero.outfit) {
    for (const [slot, v] of Object.entries(hero.outfit.defaults)) o[slot] = v;
  }
  if (!equipment) return o;
  for (const slot of EQUIP_SLOTS) {
    const kind = equipment[slot];
    if (kind === null || kind === undefined) continue;
    const def = catalog.find((d) => d.kind === kind);
    if (!def) continue; // unknown kind — render the bare slot
    o[def.heroSlot] = def.heroVariant;
  }
  return o;
}

/** Shared painter for game, lab and Gear preview. Resolve all images first;
 * incomplete frames never expose the covered body/cosmetic layers. */
export function paintHeroParts(
  ctx: CanvasRenderingContext2D,
  hero: HeroSprite,
  parts: readonly HeroPart[],
  resolve: (part: HeroPart) => CanvasImageSource | null,
  ox: number,
  oy: number,
  scale = 1,
): boolean {
  const images: { part: HeroPart; image: CanvasImageSource }[] = [];
  for (const part of parts) {
    const image = resolve(part);
    if (!image) return false;
    images.push({ part, image });
  }
  for (const { part, image } of images) {
    const { dims: [w, h], offset: [x, y] } = heroPartBounds(hero, part);
    ctx.drawImage(image, ox + x * scale, oy + y * scale, w * scale, h * scale);
  }
  return images.length > 0;
}

function drawHeroParts(
  ctx: CanvasRenderingContext2D, assets: HeroAssets, tint: string,
  parts: HeroPart[], anim: string, dir: Dir, frame: number, ox: number, oy: number,
): boolean {
  return paintHeroParts(ctx, assets.hero, parts, (p) => {
    const paths = assets.hero.outfit?.frames[tint]?.[p.slot]?.[p.variant]?.[p.layer]?.[anim]?.[dir];
    const fp = paths?.[Math.min(frame, paths.length - 1)];
    return fp ? heroPartImg(assets, fp, `${tint}:${p.slot}:${p.variant}:${p.layer}:${anim}:${dir}:${frame}`) : null;
  }, ox, oy);
}

export interface HeroComposite extends PartBounds {
  canvas: HTMLCanvasElement;
  /** Feet anchor inside this raster. The body's world anchor is unchanged. */
  anchor: [number, number];
}

/**
 * Composited hero frames: composite key → the canvas, LRU-bounded.
 *
 * The composite is the single most expensive hero operation by a wide margin.
 * Measured (software raster, 96×96 sheet): ~0.55 ms per hero per FRAME on the
 * account-look path against ~0.01 ms for the seven part blits it replaces —
 * getImageData + a per-pixel table walk + putImageData, every frame. But its
 * inputs only change when the hero changes pose, facing, gear or look, so at
 * 60 fps a walking hero redoes the same composite ~10 times per animation
 * frame. Cached, the steady state is one blit of a canvas.
 *
 * Two rules keep it honest:
 *  - Only SUCCESSFUL composites are stored. A part still decoding (or just
 *    evicted from the pack) returns without caching, so a half-drawn hero can
 *    never freeze in place.
 *  - The key covers every input the pixels depend on, including the sprite
 *    store's generation (a reinstalled pack is different art under the same
 *    paths) and the look's `sig`.
 *
 * A side benefit: a cache hit returns a STABLE canvas identity, which is what
 * lets the renderer cache the things it derives from the composite (the sprint
 * outline) instead of re-dilating a fresh one every frame.
 *
 * Both a 96-entry ceiling and an 8 MiB pixel budget bound the cache, including
 * parts that extend beyond the 96×96 body grid.
 */
const compCache = new Map<string, HeroComposite>();
const COMP_CACHE_BYTES = 8 * 1024 * 1024;
let compBytes = 0;
const heroIds = new WeakMap<HeroSprite, number>();
let nextHeroId = 0;
const COMP_CACHE_LIMIT = 96;

/** Drop every cached composite (a palette/look reload, or tests). */
export function invalidateHeroComposites(): void {
  compCache.clear();
  compBytes = 0;
}

/**
 * The cache identity of one composite — every input the pixels depend on, in
 * one string. Exported so a caller can key what it DERIVES from the composite
 * (the sprint rim in markers.ts) on the same identity; JSON.stringify of the
 * outfit costs microseconds against the composite it skips.
 */
export function heroCompositeKey(
  assets: HeroAssets,
  tint: string,
  equipped: Outfit,
  anim: string,
  dir: Dir,
  frame: number,
  appearance: HeroLut | null = null,
): string {
  const [dw, dh] = assets.hero.dims;
  let id = heroIds.get(assets.hero);
  if (id === undefined) { id = ++nextHeroId; heroIds.set(assets.hero, id); }
  const store = spriteStore();
  return `${id}|${assets.atlas.version}|${store ? store.generation : "loose"}|${tint}|${dw}x${dh}|${anim}|${dir}|${frame}|${appearance ? appearance.sig : ""}|${JSON.stringify(equipped)}`;
}

/**
 * Composite the hero's parts into a 1:1 (source-pixel) canvas — applying the
 * account look LUT when one is present — and return its bounds and anchor,
 * or null when the hero
 * has no outfit data (the legacy v5 shell) or the outfit resolves to nothing.
 *
 * The returned composite is CACHED and must be treated as read-only; it stays
 * valid across other heroes' composites (callers blit it immediately).
 *
 * The renderer's Sprint / Blink passes need a WHOLE-BODY silhouette: the
 * hero is 6–8 stacked part frames, and outlining the parts individually
 * would trace every layer seam. One composited canvas is what makes a single
 * clean outline (and cheap ghost copies of it) possible.
 */
export function heroComposite(
  assets: HeroAssets,
  tint: string,
  equipped: Outfit,
  anim: string,
  dir: Dir,
  frame: number,
  appearance: HeroLut | null = null,
): HeroComposite | null {
  const hero = assets.hero;
  const key = heroCompositeKey(assets, tint, equipped, anim, dir, frame, appearance);
  const hit = compCache.get(key);
  if (hit) {
    compCache.delete(key); // LRU touch: re-insert as the newest entry
    compCache.set(key, hit);
    return hit;
  }
  const bounds = heroOutfitBounds(hero, equipped, tint);
  const [dw, dh] = bounds.dims;
  const [bx, by] = bounds.offset;
  const parts = outfitParts(hero, equipped, dir, tint);
  if (parts.length === 0) return null;
  const c = document.createElement("canvas");
  c.width = dw;
  c.height = dh;
  const sctx = c.getContext("2d");
  if (!sctx) return null;
  if (!drawHeroParts(sctx, assets, tint, parts, anim, dir, frame, -bx, -by)) return null;
  if (appearance) {
    const data = sctx.getImageData(0, 0, dw, dh);
    applyLutToPixels(data.data, appearance.lut);
    sctx.putImageData(data, 0, 0);
  }
  const composite: HeroComposite = { canvas: c, ...bounds, anchor: [hero.anchor[0] - bx, hero.anchor[1] - by] };
  compCache.set(key, composite);
  compBytes += dw * dh * 4;
  while (compCache.size > COMP_CACHE_LIMIT || compBytes > COMP_CACHE_BYTES) {
    const oldest = compCache.keys().next();
    if (oldest.done) break;
    const old = compCache.get(oldest.value)!;
    compBytes -= old.dims[0] * old.dims[1] * 4;
    compCache.delete(oldest.value);
  }
  return composite;
}

export function drawHero(
  ctx: CanvasRenderingContext2D,
  assets: HeroAssets,
  tint: string,
  equipped: Outfit,
  anim: string,
  dir: Dir,
  frame: number,
  cx: number,
  cy: number,
  u: number,
  appearance: HeroLut | null = null,
): void {
  const c = heroComposite(assets, tint, equipped, anim, dir, frame, appearance);
  if (c) ctx.drawImage(c.canvas, cx - c.anchor[0] * u, cy - c.anchor[1] * u, c.dims[0] * u, c.dims[1] * u);
}


/** Resolve + cache one part-frame image (returns the element even while it
 *  loads). The game path never awaits this — its never-bail last-drawn
 *  fallback covers gaps; the editor preview does await it, so it needs the
 *  element to hang onload on. */
export function partFrameImg(assets: HeroAssets, path: string): HTMLImageElement {
  const packed = spriteStore();
  if (packed?.has(path)) return packed.image(path);
  let im = assets.imgCache.get(path);
  if (!im) {
    im = new Image();
    im.src = frameUrl(path, assets.atlas.version);
    assets.imgCache.set(path, im);
  }
  return im;
}

/** M4 dropped-item icon URL for an item id + tint (one frame per tint).
 *  Resolves the path even when the image hasn't loaded yet — callers that
 *  build their own <img> elements (the HUD grid) must not gate on load
 *  state: a cell is built once per signature change, so a loaded-only
 *  check would leave the first build empty with no retry. Null when the
 *  atlas has no items section or the item/tint is unknown. */
export function itemFrameUrl(assets: HeroAssets, itemId: string, tint: string): string | null {
  const def = assets.atlas.items?.[itemId];
  const paths = def?.frames?.[tint] ?? def?.frames?.["canon"];
  const p = paths?.[0];
  if (!p) return null;
  const packed = spriteStore();
  return packed?.has(p) ? packed.url(p) : frameUrl(p, assets.atlas.version);
}

/** M4 dropped-item icon for an item id + tint (one frame per tint; the
 *  inventory icon IS the dropped item). Loaded-only — the renderer calls
 *  this every frame, so a transient load gap retries naturally. Slot
 *  includes the item id so a missing/unknown item never blanks a
 *  previously-drawn one. Null when the atlas has no items section (or the
 *  tint is absent) — callers leave the slot empty until the catalog's real
 *  art is available; no content-shaped placeholder is invented. */
export function itemFrameImg(
  assets: HeroAssets,
  itemId: string,
  tint: string,
): HTMLImageElement | null {
  const def = assets.atlas.items?.[itemId];
  const paths = def?.frames?.[tint] ?? def?.frames?.["canon"];
  if (!paths?.length) return null;
  const p = paths[0];
  if (!p) return null;
  return holdFrame(assets, `item:${itemId}:${tint}`, imgFor(assets, p));
}

/** Fetch + parse the committed atlas from the game path. Null on any failure
 *  (players and item drops draw nothing until their committed art is ready;
 *  the catalog never substitutes letters or generic shapes for content). */
let sharedGameAssets: Promise<HeroAssets | null> | null = null;

export function loadHeroAssets(options: { preloadOutfits?: boolean } = {}): Promise<HeroAssets | null> {
  // Hub and floor are separate scenes but one page. Reuse the parsed atlas
  // and image state when the packaged game path is active.
  if (spriteStore()) return sharedGameAssets ??= fetchHeroAssets();
  return fetchHeroAssets(options.preloadOutfits ?? true);
}

async function fetchHeroAssets(preloadOutfits = true): Promise<HeroAssets | null> {
  try {
    const packed = spriteStore();
    let json: unknown;
    if (packed) json = JSON.parse(await packed.text("atlas.json"));
    else {
      const r = await fetch(`/sprites/atlas.json?v=${Date.now()}`);
      if (!r.ok) return null;
      json = await r.json();
    }
    const parsed = parseAtlas(json);
    if (!parsed.ok) return null;
    const hero = parsed.atlas.sprites.hero;
    if (!hero || hero.tile) return null;
    const assets: HeroAssets = { atlas: parsed.atlas, hero, imgCache: new Map(), lastDrawn: new Map() };
    if (!packed && preloadOutfits) preloadDefaultOutfit(assets);
    preloadItemIcons(assets);
    return assets;
  } catch {
    return null;
  }
}

function collectPngPaths(value: unknown, out: Set<string>): void {
  if (typeof value === "string") {
    if (value.endsWith(".png")) out.add(value);
  } else if (Array.isArray(value)) {
    for (const item of value) collectPngPaths(item, out);
  } else if (value && typeof value === "object") {
    for (const item of Object.values(value)) collectPngPaths(item, out);
  }
}

/** Decode the first scene's art before removing the loading screen. The
 * compressed package holds every variant, but opening thousands of blob URLs
 * at once exhausts Firefox's native descriptors on macOS. Later frames decode
 * from local bytes as needed; drawHeroParts waits for a complete composite. */
export async function preloadGameArt(assets: HeroAssets, progress?: (done: number, total: number) => void): Promise<void> {
  const packed = spriteStore();
  if (!packed) return;
  const all = new Set<string>();
  const pinned = new Set<string>();
  const outfit = assets.hero.outfit;
  if (outfit) {
    const defaults = outfit.defaults;
    for (const anim of ["idle", "walk"]) {
      for (const dir of DIRS) {
        for (const part of outfitParts(assets.hero, defaults, dir, "canon")) {
          const paths = outfit.frames.canon?.[part.slot]?.[part.variant]?.[part.layer]?.[anim]?.[dir] ?? [];
          for (const path of paths) {
            all.add(path);
            pinned.add(path);
          }
        }
      }
    }
  }
  collectPngPaths(assets.atlas.npcs?.["bull"]?.frames?.["canon"]?.["idle"], all);
  collectPngPaths(assets.atlas.npcs?.["bull"]?.frames?.["canon"]?.["idle"], pinned);
  collectPngPaths(assets.atlas.items, all);
  collectPngPaths(assets.atlas.items, pinned);
  collectPngPaths(assets.atlas.props, all);
  collectPngPaths(assets.atlas.props, pinned);
  for (const sprite of Object.values(assets.atlas.sprites)) {
    if (sprite.tile) {
      collectPngPaths(sprite.frames, all);
      collectPngPaths(sprite.frames, pinned);
    }
  }
  all.add("corpses/bones.png");
  pinned.add("corpses/bones.png");
  packed.pin(pinned);
  await packed.preload(all, progress);
}

/** Kick off loads of the FLOOR item icons (the key, both gatestones) as soon
 *  as the atlas lands. The icons are tiny and this keeps the first visible
 *  floor frame aligned with the same real art used by inventory cells;
 *  itemFrameImg's last-drawn cache only bridges an image decode, never a
 *  missing catalog entry with a letter/card substitute. */
function preloadItemIcons(assets: HeroAssets): void {
  const items = assets.atlas.items;
  if (!items) return;
  const preload = (itemId: string, tint: string): void => {
    const def = items[itemId];
    if (!def) return;
    const paths = def.frames?.[tint] ?? def.frames?.["canon"];
    for (const p of paths ?? []) imgFor(assets, p);
  };
  preload("key", "canon");
  preload("gatestone-group", "canon");
  // Personal gatestones tint by owner identity (p0..p4) — preload every tint.
  for (let i = 0; i < PLAYER_TINT_COUNT; i++) preload("gatestone-personal", playerTint(i));
}

/** v6: kick off background loads of the default outfit's part frames for
 *  every tint (~3k small PNGs) so the game's first frame is a complete
 *  composite, not a partial one (same never-pop contract as the lab's
 *  preload). Fire-and-forget — drawHero's last-drawn cache covers a decode
 *  gap, while missing content remains absent rather than becoming a glyph. */
function preloadDefaultOutfit(assets: HeroAssets): void {
  const outfit = assets.hero.outfit;
  if (!outfit) return;
  // Preload the bare hero AND every catalog item's parts (each rendered as
  // an outfit over the defaults — outfitParts resolves the part list, no
  // path guesswork). The spawn outfit is a subset of this union; the staff
  // is the one part not worn at spawn and the first equip must not blank.
  const outfits: Outfit[] = [outfit.defaults];
  for (const def of ITEMS) outfits.push({ ...outfit.defaults, [def.heroSlot]: def.heroVariant });
  for (const tint of assets.hero.tints) {
    for (const anim of Object.keys(assets.hero.anims)) {
      for (const d of DIRS) {
        for (const o of outfits) {
          for (const p of outfitParts(assets.hero, o, d, tint)) {
            const paths = outfit.frames[tint]?.[p.slot]?.[p.variant]?.[p.layer]?.[anim]?.[d];
            for (const fp of paths ?? []) imgFor(assets, fp);
          }
        }
      }
    }
  }
}
